import { createFileRoute } from "@tanstack/react-router";
import { Phone, Check, DollarSign, Home, CloudRain, Grid3x3, Layers, Rows3, Droplets, Shield, AppWindow } from "lucide-react";
import { openQuoteModal } from "../components/quote-modal";
import { Reveal } from "../components/reveal";
import { Breadcrumbs } from "../components/breadcrumbs";
import { InternalLinkRow, POPULAR_SERVICE_LINKS, CORE_CITY_LINKS } from "@/components/internal-links";
import { InlineQuoteSection } from "@/components/inline-quote-section";

const SITE = "https://capitalpropressurewashing.com";
const TITLE = "Pressure Washing Prices & Cost Guide | Capital Pro Pressure Washing";
const DESC =
  "Transparent pressure washing prices for the Capital Region, NY. See cost ranges for house washing, roof cleaning, gutter cleaning, driveway cleaning, and more. Get your free quote.";

type PriceRow = {
  service: string;
  slug: string;
  icon: typeof Home;
  range: string;
  blurb: string;
};

const PRICE_TABLE: PriceRow[] = [
  {
    service: "House Washing",
    slug: "house-washing",
    icon: Home,
    range: "$250 – $600+",
    blurb: "Depends on square footage, number of stories, and siding type. Most standard Colonials and ranches fall in the $300–$450 range.",
  },
  {
    service: "Roof Washing",
    slug: "roof-washing",
    icon: CloudRain,
    range: "$350 – $900+",
    blurb: "Soft-wash roof cleaning. Price scales with roof pitch, square footage, and how heavy the black streaks (Gloeocapsa magma) are.",
  },
  {
    service: "Concrete & Driveway Cleaning",
    slug: "concrete-cleaning-sealing",
    icon: Grid3x3,
    range: "$200 – $500+",
    blurb: "Includes surface cleaning and optional sealing. Longer driveways or heavy oil staining push toward the higher end.",
  },
  {
    service: "Paver Cleaning & Sealing",
    slug: "paver-cleaning-sealing",
    icon: Layers,
    range: "$300 – $800+",
    blurb: "Cleaning plus joint sand and sealer. Polymeric sand upgrade and wet-look sealer add to the total.",
  },
  {
    service: "Deck & Patio Cleaning",
    slug: "deck-patio-cleaning",
    icon: Rows3,
    range: "$200 – $500+",
    blurb: "Wood, composite, or stamped concrete. Stain removal and optional sealing or brightener affect the final price.",
  },
  {
    service: "Gutter Cleaning",
    slug: "gutter-cleaning",
    icon: Droplets,
    range: "$150 – $400+",
    blurb: "Based on linear feet of gutter, number of stories, and how clogged they are.",
  },
  {
    service: "Gutter Guards",
    slug: "gutter-guards",
    icon: Shield,
    range: "$400 – $1,200+",
    blurb: "Material and linear feet of gutter drive the cost. We recommend and install the guard type that fits your roofline.",
  },
  {
    service: "Window Cleaning",
    slug: "window-cleaning",
    icon: AppWindow,
    range: "$150 – $500+",
    blurb: "Interior, exterior, or both. Count and accessibility (storm windows, height) determine the final price.",
  },
];

const FACTORS = [
  { title: "Size of the Area", desc: "Square footage is the biggest factor. A 3,000 sq ft Colonial costs more than a 1,200 sq ft ranch — more surface, more solution, more time." },
  { title: "Number of Stories", desc: "Two-story and three-story homes require more setup time and equipment. Each additional story adds to the cost." },
  { title: "Severity of Staining", desc: "Light seasonal grime cleans up fast. Heavy algae, moss, rust, or oil stains take more dwell time, detergent, and passes." },
  { title: "Accessibility", desc: "Steep roofs, tight backyards, or hard-to-reach areas may require additional setup. Easy-access homes cost less." },
  { title: "Combination Services", desc: "Bundling services — like house wash + driveway + gutters — almost always costs less per service than booking them separately." },
  { title: "Condition of Surfaces", desc: "Older surfaces with fragile paint or delicate materials may need extra care and a gentler approach, which can affect timing." },
];

const FAQS = [
  {
    q: "How much does pressure washing cost in the Capital Region?",
    a: "Most house washes in the Albany area run $300–$500 depending on size and staining. Driveway cleaning typically adds $200–$350, and roof soft-washing starts around $350. The best way to know your exact price is a free quote — we measure your property and give you a straight number.",
  },
  {
    q: "Do you charge by the square foot?",
    a: "We price by the job, not by the hour. Square footage is a factor, but we also consider the number of stories, surface type, and how much buildup there is. You get one all-in price with no surprise add-ons.",
  },
  {
    q: "Is there a minimum charge?",
    a: "Yes, most jobs start around $150 to cover equipment, travel, and setup. Small jobs — like a single walkway or a few gutters — may hit the minimum. Bundling services gets you the most value.",
  },
  {
    q: "Can I get a price without someone coming out?",
    a: "Often, yes. We can quote many jobs remotely using Google Earth measurements and photos you send. If your property needs an on-site look, we come out free of charge.",
  },
  {
    q: "Do you offer discounts?",
    a: "Yes — we offer military and first responder discounts. We also run seasonal specials. Check our current offers page or ask when you request your quote.",
  },
  {
    q: "Do you offer financing?",
    a: "Yes. Qualified customers can finance projects starting at $500 with 6 months no interest. Checking your options is a soft credit pull and takes about a minute.",
  },
];

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/pricing` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: `${SITE}/pricing` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: PricingPage,
});

function PricingPage() {
  return (
    <main>
      <Breadcrumbs items={[{ label: "Pricing" }]} />

      {/* Hero */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4 text-center">
          <p
            className="font-semibold uppercase tracking-[0.2em] text-sm mb-3"
            style={{ fontFamily: "var(--font-display)", color: "#ff6b7f" }}
          >
            Capital Region Cost Guide
          </p>
          <h1
            className="text-3xl md:text-5xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Pressure Washing Prices
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Ballpark ranges for every service we offer. No hidden fees, no surprise
            add-ons — just straight numbers so you know what to expect before you call.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Get Your Exact Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
          <p className="mt-5 text-white/70 text-sm">
            Every quote is free and no-obligation — most in under one business day.
          </p>
        </div>
      </section>

      {/* Price table */}
      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[1000px] px-4">
          <div className="text-center mb-8 md:mb-10">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              What Services Cost
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Price Ranges by Service
            </h2>
            <p className="mt-3 text-[var(--brand-charcoal)]/70 text-sm md:text-base max-w-2xl mx-auto">
              These are typical ranges for the Capital Region. Your actual price depends on
              your property — get a free quote for an exact number.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-[var(--brand-deep-blue)]">
                  <th className="text-left py-3 px-4 text-[var(--brand-deep-blue)] uppercase text-sm md:text-base" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>Service</th>
                  <th className="text-right py-3 px-4 text-[var(--brand-deep-blue)] uppercase text-sm md:text-base whitespace-nowrap" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>Typical Range</th>
                </tr>
              </thead>
              <tbody>
                {PRICE_TABLE.map((row, i) => {
                  const Icon = row.icon;
                  return (
                    <tr
                      key={row.service}
                      className={i % 2 === 0 ? "bg-[var(--brand-light-gray)]" : "bg-white"}
                    >
                      <td className="py-4 px-4 align-top">
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-md bg-[var(--brand-red)]/10 text-[var(--brand-red)] flex items-center justify-center shrink-0 mt-0.5">
                            <Icon size={18} />
                          </div>
                          <div>
                            <a
                              href={`/${row.slug}`}
                              className="text-[var(--brand-deep-blue)] text-base md:text-lg font-bold uppercase leading-tight hover:text-[var(--brand-bright-blue)]"
                              style={{ fontFamily: "var(--font-display)" }}
                            >
                              {row.service}
                            </a>
                            <p className="mt-1 text-[var(--brand-charcoal)]/75 text-sm leading-relaxed">
                              {row.blurb}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-right align-top whitespace-nowrap">
                        <span
                          className="text-[var(--brand-deep-blue)] text-lg md:text-xl font-bold"
                          style={{ fontFamily: "var(--font-display)" }}
                        >
                          {row.range}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-center text-[var(--brand-charcoal)]/60 text-xs md:text-sm">
            Prices are estimates for the Albany / Capital Region area. Your final quote may
            vary based on your specific property and needs.
          </p>
        </div>
      </section>

      {/* Factors */}
      <section className="bg-[var(--brand-light-gray)] py-14 md:py-16">
        <div className="mx-auto max-w-[1000px] px-4">
          <div className="text-center mb-8 md:mb-10">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              What Affects Price
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              6 Factors We Look At
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FACTORS.map((f, i) => (
              <Reveal
                key={f.title}
                delayMs={i * 60}
                className="card-pop bg-white rounded-lg p-6 border border-gray-200"
              >
                <div className="w-11 h-11 rounded-md bg-[var(--brand-deep-blue)]/10 text-[var(--brand-deep-blue)] flex items-center justify-center mb-4">
                  <DollarSign size={22} />
                </div>
                <h3
                  className="text-[var(--brand-deep-blue)] text-lg uppercase mb-2 leading-tight"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {f.title}
                </h3>
                <p className="text-[var(--brand-charcoal)]/85 text-sm leading-relaxed">
                  {f.desc}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Bundle CTA */}
      <section className="bg-white py-12 md:py-14">
        <div className="mx-auto max-w-[1000px] px-4">
          <Reveal className="rounded-lg bg-[var(--brand-deep-blue)] text-white p-6 md:p-10 text-center">
            <h2
              className="text-2xl md:text-3xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Bundle & Save
            </h2>
            <p className="mt-3 text-white/85 text-base max-w-2xl mx-auto leading-relaxed">
              Combining services — like a house wash, driveway clean, and gutter flush —
              almost always costs less per service than booking them one at a time. Ask
              about combo pricing when you get your quote.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={() => openQuoteModal()}
                className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
                style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
              >
                Get Free Quote
              </button>
              <a
                href="tel:+15189001913"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
                style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
              >
                <Phone size={18} strokeWidth={2.5} />
                Call (518) 900-1913
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Pricing FAQ */}
      <section className="bg-[var(--brand-light-gray)] py-14 md:py-16">
        <div className="mx-auto max-w-[900px] px-4">
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight text-center mb-8"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Pricing Questions
          </h2>
          <div className="space-y-4">
            {FAQS.map((f) => (
              <details
                key={f.q}
                className="group bg-white rounded-lg border border-gray-200 p-5"
              >
                <summary
                  className="cursor-pointer list-none text-[var(--brand-deep-blue)] text-base md:text-lg uppercase leading-tight"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {f.q}
                </summary>
                <p className="mt-3 text-[var(--brand-charcoal)]/85 text-sm md:text-base leading-relaxed">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-12">
        <div className="mx-auto max-w-[1200px] px-4 text-center">
          <h2
            className="text-2xl md:text-3xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Ready For Your Free Quote?
          </h2>
          <p className="mt-2 text-white/85">
            Tell us what you need cleaned. We'll give you a straight price — no obligation.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Get Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-4 text-white/70 text-sm">
            <span className="inline-flex items-center gap-1.5">
              <Check size={16} className="text-[#ff6b7f]" strokeWidth={3} />
              Licensed & Insured
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check size={16} className="text-[#ff6b7f]" strokeWidth={3} />
              10+ Years Local
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check size={16} className="text-[#ff6b7f]" strokeWidth={3} />
              Military & First Responder Discounts
            </span>
          </div>
        </div>
      </section>
      <InternalLinkRow
        eyebrow="Explore The Site"
        title="Popular Services"
        links={POPULAR_SERVICE_LINKS}
      />

      <InternalLinkRow
        tone="white"
        eyebrow="Where We Work"
        title="Areas We Serve"
        links={CORE_CITY_LINKS}
      />

      <InlineQuoteSection source="Pricing" />
    </main>
  );
}
